/**
 * One programme's team, and every write that changes it.
 *
 * Split out so the screen above is composition and the reads, the writes and
 * the feedback they produce are testable without rendering it.
 *
 * @module components/teams/useTeamAssignments
 */

import { useCallback, useEffect, useRef, useState } from "react";

import type { ResponsibilityGroup } from "@/components/teams/team-assignments";
import type { ProgrammePlatformClient } from "@/platform";
import type {
  ModuleOwnershipAssignment,
  Programme,
  ProgrammeResponsibility,
  ProgrammeResponsibilityAssignment,
} from "@/server/types";
import { begin } from "@/utils/begin";

export type ActionStatus = "idle" | "working" | "success" | "error";

export interface ActionFeedback {
  status: ActionStatus;
  message: string;
}

/** One programme's assignments, tagged with the programme they were loaded for. */
interface LoadedAssignments {
  programmeId: string;
  programme: Programme | null;
  responsibilities: ProgrammeResponsibilityAssignment[];
  moduleOwners: ModuleOwnershipAssignment[];
  error: Error | null;
}

/** Stable identities, so a stale or unloaded programme does not churn the lists. */
const NO_RESPONSIBILITIES: ProgrammeResponsibilityAssignment[] = [];
const NO_MODULE_OWNERS: ModuleOwnershipAssignment[] = [];

function errorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : "The assignment operation failed";
}

export interface TeamAssignments {
  programme: Programme | null;
  responsibilities: readonly ProgrammeResponsibilityAssignment[];
  /** Only owners who still hold a write responsibility on the programme. */
  activeModuleOwners: readonly ModuleOwnershipAssignment[];
  loading: boolean;
  loadError: Error | null;
  /** Reads a failed programme again, loading rather than showing the failure meanwhile. */
  retry: () => void;
  feedback: ActionFeedback;
  clearFeedback: () => void;
  addStaffMember: (userId: string, responsibility: ProgrammeResponsibility) => void;
  toggleResponsibility: (
    group: ResponsibilityGroup,
    responsibility: ProgrammeResponsibility,
  ) => void;
  assignModuleOwner: (moduleId: string, userId: string) => void;
  revokeModuleOwner: (ownership: ModuleOwnershipAssignment) => void;
}

export interface TeamAssignmentsOptions {
  client: ProgrammePlatformClient;
  programmeId: string;
  onChanged?: () => Promise<void> | void;
}

export function useTeamAssignments({
  client,
  programmeId,
  onChanged = undefined,
}: TeamAssignmentsOptions): TeamAssignments {
  // One record rather than four states, tagged with the programme it was loaded
  // for. Anything naming another programme is stale by definition, so the
  // loading and empty states fall out of a comparison instead of needing a
  // synchronous clear every time the programme moves.
  const [loaded, setLoaded] = useState<LoadedAssignments | null>(null);
  const [feedback, setFeedback] = useState<ActionFeedback>({ status: "idle", message: "" });

  const settled = Boolean(programmeId) && loaded?.programmeId === programmeId;
  const programme = settled ? loaded.programme : null;
  const responsibilities = settled ? loaded.responsibilities : NO_RESPONSIBILITIES;
  const moduleOwners = settled ? loaded.moduleOwners : NO_MODULE_OWNERS;

  // Fetch and publish. Every update lands in a continuation, so the effect
  // below can call it without cascading a render, and the record carries the
  // programme it belongs to rather than being cleared on the way in.
  // The same guard `useAsync` carries. The tag says which programme a reply
  // belongs to, not which reply is newest: a late one for the programme just
  // left overwrites the current record, and the tag then names the wrong
  // programme, so the pane reads as loading with nothing left to re-fire it.
  const generation = useRef(0);
  // The programme in force, readable from a handler made in an earlier render:
  // that handler's `refresh` reloads the programme its press named, not this one.
  const current = useRef(programmeId);
  const refresh = useCallback((): Promise<void> => {
    if (!programmeId) {
      return Promise.resolve();
    }
    generation.current += 1;
    const mine = generation.current;
    return begin(() =>
      Promise.allSettled([
        client.loadProgramme(programmeId),
        client.listProgrammeResponsibilities(programmeId),
        client.listModuleOwners(programmeId),
      ]),
    )
      .then(([record, fetchedResponsibilities, fetchedModuleOwners]) => {
        if (generation.current !== mine) {
          return;
        }
        // The record decides first: a programme gone or hidden refuses its
        // team reads too, and their refusal would hide this message.
        if (record.status === "rejected") {
          throw record.reason;
        }
        if (!record.value) {
          throw new Error("The selected programme is no longer available");
        }
        if (fetchedResponsibilities.status === "rejected") {
          throw fetchedResponsibilities.reason;
        }
        if (fetchedModuleOwners.status === "rejected") {
          throw fetchedModuleOwners.reason;
        }
        setLoaded({
          programmeId,
          programme: record.value,
          responsibilities: fetchedResponsibilities.value,
          moduleOwners: fetchedModuleOwners.value,
          error: null,
        });
      })
      .catch((cause: unknown) => {
        if (generation.current !== mine) {
          return;
        }
        setLoaded({
          programmeId,
          programme: null,
          responsibilities: [],
          moduleOwners: [],
          error: cause instanceof Error ? cause : new Error(errorMessage(cause)),
        });
      });
  }, [client, programmeId]);

  useEffect(() => {
    current.current = programmeId;
    refresh();
  }, [programmeId, refresh]);

  const run = async (successMessage: string, action: () => Promise<unknown>): Promise<void> => {
    const wrote = programmeId;
    setFeedback({ status: "working", message: "Saving assignment changes..." });
    try {
      await action();
      // The caller is told either way: the write happened, whatever this panel
      // is showing by now, and `onChanged` refreshes the whole workspace. Only
      // this panel is guarded, because its reload would publish the old
      // programme's team under the new one's header.
      await onChanged?.();
      if (wrote !== current.current) {
        return;
      }
      await refresh();
      setFeedback({ status: "success", message: successMessage });
    } catch (cause) {
      if (wrote === current.current) {
        setFeedback({ status: "error", message: errorMessage(cause) });
      }
    }
  };

  // A module owner who has lost their write responsibility is no longer on the
  // programme in any sense that matters, so they leave the workload too.
  const writers = new Set(
    responsibilities
      .filter((one) => one.responsibility === "builder" || one.responsibility === "lead")
      .map((one) => one.userId),
  );
  const activeModuleOwners = moduleOwners.filter((owner) => writers.has(owner.userId));

  return {
    programme,
    responsibilities,
    activeModuleOwners,
    loading: Boolean(programmeId) && !settled,
    loadError: settled ? loaded.error : null,
    retry: () => {
      setLoaded(null);
      refresh();
    },
    feedback,
    clearFeedback: () => {
      setFeedback({ status: "idle", message: "" });
    },
    addStaffMember: (userId, responsibility) => {
      if (!programmeId || !userId) {
        return;
      }
      if (responsibilities.some((assignment) => assignment.userId === userId)) {
        setFeedback({
          status: "error",
          message: "This staff member is already on the programme team.",
        });
        return;
      }
      run("Staff member added to the programme team.", () =>
        client.assignProgrammeResponsibility({ programmeId, userId, responsibility }),
      );
    },
    toggleResponsibility: (group, responsibility) => {
      const assignment = group.assignments.find(
        (candidate) => candidate.responsibility === responsibility,
      );
      if (assignment) {
        run("Programme responsibility removed.", () =>
          client.revokeProgrammeResponsibility({
            programmeId: assignment.programmeId,
            assignmentId: assignment.id,
          }),
        );
        return;
      }
      run("Programme permission added.", () =>
        client.assignProgrammeResponsibility({
          programmeId,
          userId: group.userId,
          responsibility,
        }),
      );
    },
    assignModuleOwner: (moduleId, userId) => {
      if (!programmeId || !moduleId || !userId) {
        return;
      }
      // The first builder is what moves a ready programme into development, so
      // the message says so rather than leaving the state change unexplained.
      const unlocks =
        activeModuleOwners.length === 0 && programme?.workflowState === "Ready for Development";
      run(
        unlocks
          ? "Module Builder assigned. The programme is now In Development."
          : "Module builder assignment saved.",
        // The server grants `builder` with the assignment, in one transaction.
        () => client.assignModuleOwner({ programmeId, moduleId, userId }),
      );
    },
    revokeModuleOwner: (ownership) => {
      run("Module builder assignment removed.", () =>
        client.revokeModuleOwner({
          programmeId: ownership.programmeId,
          moduleId: ownership.moduleId,
        }),
      );
    },
  };
}
